import React from "react";
import { motion } from "framer-motion";
import { Dumbbell, Zap, Apple, Users, ArrowUpRight } from "lucide-react";
import { ASSETS, PROGRAMS } from "../lib/assets";

const ICONS = { Zap, Dumbbell, Apple, Users };

export const Programs = () => {
  return (
    <section id="programs" data-testid="programs-section" className="relative py-24 md:py-32 border-t border-white/10 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-6">/ 02 — What We Do</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-none max-w-3xl">
              WHERE MOVEMENT <span className="text-[#C4F542]">CHANGES</span> LIVES.
            </h2>
          </div>
          <p className="text-base text-zinc-400 font-body max-w-md">
            Every program at Our HOVSE Foundation is built around four core pillars: Movement,
            Resilience, Community, and Discipline. Through fitness, mentorship, and
            social-emotional learning (SEL), we help young people build confidence, strengthen
            character, and develop the leadership skills they need to thrive in school, at home,
            and in life.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Big training image card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-4 md:row-span-2 relative overflow-hidden border border-white/10 group flex flex-col md:block md:min-h-[400px]"
          >
            {/* Image */}
            <div className="relative w-full h-72 sm:h-96 md:h-full md:absolute md:inset-0 overflow-hidden">
              <img
                src={ASSETS.proclamation}
                alt="Philly Weeden Day proclamation"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              {/* Desktop-only gradient overlay */}
              <div className="hidden md:block absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            </div>

            {/* Text — stacked below on mobile, overlaid on desktop */}
            <div className="bg-[#0A0A0A] md:bg-transparent p-6 sm:p-8 md:p-10 md:absolute md:inset-0 md:flex md:flex-col md:justify-end md:pointer-events-none">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#C4F542] mb-2 md:mb-3">Featured Highlight</p>
              <h3 className="font-display text-3xl sm:text-4xl md:text-5xl text-white mb-2 md:mb-3 leading-tight">PHILLY WEEDEN DAY</h3>
              <p className="text-sm sm:text-base text-zinc-300 max-w-md font-body leading-snug">
                On Aug 17, 2026, Founder Philly Weeden received a proclamation from Mayor Jim Petras of Cleveland Heights for his nearly three decades of impact and fitness in the community.
              </p>
            </div>
          </motion.div>

          {/* Program cards */}
          {PROGRAMS.map((p, i) => {
            const Icon = ICONS[p.icon];
            return (
              <motion.div
                key={p.key}
                data-testid={`program-${p.key}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`group bg-[#111111] border border-white/10 p-6 md:p-7 hover:border-[#C4F542] hover:-translate-y-1 transition-all duration-300 ${
                  i === 0 || i === 1 ? "md:col-span-2" : "md:col-span-3"
                }`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="h-12 w-12 border border-[#C4F542]/30 bg-[#C4F542]/10 flex items-center justify-center">
                    <Icon size={22} className="text-[#C4F542]" />
                  </div>
                  <ArrowUpRight size={20} className="text-zinc-600 group-hover:text-[#C4F542] group-hover:rotate-12 transition-all" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white mb-3">{p.title}</h3>
                <p className="text-sm text-zinc-400 font-body leading-relaxed">{p.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
